import { Alert, AlertIcon, Badge, Box, Button, Divider, Flex, HStack, Text } from '@chakra-ui/react'
import { useApp } from '@hb/react'
import {
  CordBloodBankingRecord,
  CordBloodGasRecord,
  NeonatalRecord,
  NewbornPKURecord,
  SaveLabResultArgs,
} from '@hb/shared'
import { useCallback, useState } from 'react'
import { saveLabResult } from '../../../../backend/functions/gyn'
import { useGynCareView } from '../../../../components/Gyn/context'
import { useProfile } from '../../../../contexts'
import { useLaborCharting } from '../context'
import { CordBloodBankingSubsection, CordBloodGasSubsection } from './CordBloodSection'
import { NeonatalLabQuickview } from './NeonatalLabQuickview'
import { NewbornPKUSection } from './NewbornPKUSection'

// ─── Section heading ──────────────────────────────────────────────────────────

const SectionHeader = ({ label, badge }: { label: string; badge?: string }) => (
  <Flex align="center" gap={2} pt={2}>
    <Text
      fontFamily="Hero-New"
      fontWeight={700}
      fontSize="md"
      color="pink.700"
      _dark={{ color: 'pink.200' }}>
      {label}
    </Text>
    {badge && (
      <Badge colorScheme="pink" fontFamily="Hero-New" fontSize="xs">
        {badge}
      </Badge>
    )}
  </Flex>
)

// ─── Main tab ─────────────────────────────────────────────────────────────────

export const NeonatalRecordTab = () => {
  const { assessmentId, user: patient } = useProfile()
  const patientId = patient?.id ?? null
  const { appName } = useApp()
  const { practiceId } = useGynCareView()
  const { neonatalRecord: existing, saveNeonatalRecord, minimumDeliveryReport } = useLaborCharting()

  const timeOfBirth = minimumDeliveryReport?.timeOfBirth

  // Local state — merged from existing on mount
  const [banking, setBanking] = useState<CordBloodBankingRecord>(existing?.cordBloodBanking ?? {})
  const [gas, setGas] = useState<CordBloodGasRecord>(existing?.cordBloodGas ?? {})
  const [pku, setPku] = useState<NewbornPKURecord>(existing?.pkuRecord ?? {})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [labQuickviewOpen, setLabQuickviewOpen] = useState(false)

  const handleSave = useCallback(async () => {
    if (!assessmentId) return
    setSaving(true)
    setError(null)
    try {
      const record: NeonatalRecord = {
        ...existing,
        cordBloodBanking: banking,
        cordBloodGas: gas,
        pkuRecord: pku,
        updatedAt: Date.now(),
      }
      await saveNeonatalRecord(record)
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save. Please try again.')
    } finally {
      setSaving(false)
    }
  }, [assessmentId, existing, banking, gas, pku, saveNeonatalRecord])

  const handleSaveNeonatalLab = useCallback(
    async (args: Partial<SaveLabResultArgs> & { labResultId: string }) => {
      if (!assessmentId || !patientId || !practiceId) return
      await saveLabResult({
        assessmentId,
        patientId,
        practiceId,
        appName,
        labType: 'other',
        labName: '',
        result: '',
        flagStatus: 'normal',
        recordType: 'neonatal',
        ...args,
      })
    },
    [assessmentId, patientId, practiceId, appName],
  )

  if (!minimumDeliveryReport?.timeOfBirth) {
    return (
      <Flex justify="center" align="center" py={12} direction="column" gap={3}>
        <Text fontFamily="Hero-New" color="gray.400" fontSize="sm">
          Neonatal record is created after the delivery report is saved.
        </Text>
      </Flex>
    )
  }

  return (
    <Box overflowY="auto" h="100%" px={4} py={4}>
      <Flex direction="column" gap={6}>
        <Flex align="center" justify="space-between">
          <Text
            fontFamily="Hero-New"
            fontWeight={700}
            fontSize="lg"
            color="pink.700"
            _dark={{ color: 'pink.200' }}>
            Neonatal Record
          </Text>
          <Button
            size="sm"
            colorScheme="teal"
            variant="outline"
            fontFamily="Hero-New"
            onClick={() => setLabQuickviewOpen(true)}>
            Lab Results
          </Button>
        </Flex>

        {existing?.updatedAt && (
          <Badge colorScheme="green" alignSelf="flex-start" fontFamily="Hero-New" fontSize="xs">
            Last saved {new Date(existing.updatedAt).toLocaleString()}
          </Badge>
        )}

        {/* §5.4.1 Cord Blood — two separated pathways */}
        <SectionHeader label="§5.4.1 Cord Blood Banking" badge="Patient-Elected" />
        <Alert status="info" borderRadius={6} py={2} fontSize="xs">
          <AlertIcon boxSize={3} />
          Cord blood banking is a patient-elected private service. Documentation confirms the
          patient's decision and chain of custody.
        </Alert>
        <CordBloodBankingSubsection record={banking} onChange={setBanking} />

        <Divider />

        <SectionHeader label="§5.4.1 Cord Blood Gases" badge="Clinical — Medical Indication" />
        <Alert status="info" borderRadius={6} py={2} fontSize="xs">
          <AlertIcon boxSize={3} />
          Cord blood gases are a clinical test ordered on medical indication (meconium, fetal
          distress, low Apgar scores). Required documentation for billing and audit defense.
        </Alert>
        <CordBloodGasSubsection record={gas} onChange={setGas} />

        <Divider />

        {/* §5.4.2 Newborn PKU */}
        <SectionHeader label="§5.4.2 Newborn PKU / State Metabolic Screen" />
        <NewbornPKUSection record={pku} onChange={setPku} timeOfBirthMs={timeOfBirth} />

        <Divider />

        {/* §5.4.3 Additional Neonatal Labs — logged via Neonatal Lab Quickview */}
        <SectionHeader label="§5.4.3 Additional Neonatal Labs" />
        <Alert status="info" borderRadius={6} py={2} fontSize="xs">
          <AlertIcon boxSize={3} />
          Bilirubin, blood glucose, and other neonatal labs are logged and tracked in the Neonatal
          Lab Results panel.
        </Alert>
        <Button
          size="sm"
          colorScheme="teal"
          fontFamily="Hero-New"
          alignSelf="flex-start"
          onClick={() => setLabQuickviewOpen(true)}>
          Open Neonatal Lab Results
        </Button>

        {error && (
          <Alert status="error" borderRadius={6} py={2} fontSize="sm">
            <AlertIcon />
            {error}
          </Alert>
        )}

        <HStack justify="flex-end">
          <Button
            colorScheme="pink"
            fontFamily="Hero-New"
            fontWeight={700}
            isLoading={saving}
            onClick={handleSave}>
            Save Neonatal Record
          </Button>
        </HStack>
      </Flex>

      {assessmentId && patientId && (
        <NeonatalLabQuickview
          assessmentId={assessmentId}
          patientId={patientId}
          isOpen={labQuickviewOpen}
          onClose={() => setLabQuickviewOpen(false)}
          onSave={handleSaveNeonatalLab}
        />
      )}
    </Box>
  )
}
